import React from 'react';
import { useParams } from 'react-router-dom';
import Page from '../components/layout/Page';
import { PageContentRow } from '../components/layout/PageContentRow';
import PageContentWrapper from '../components/layout/PageContentWrapper';
import { useFetchBrowse } from '../hooks/useFetchData';
import { useTranslation } from '@/hooks/useTranslation';
import { MainContent } from '../components/layout/MainContent';
import BrowseContent from '../components/BrowseContent';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useNodes } from '@/hooks/useNodes';
import { useMetaTitle } from '@/hooks/useMetaTitle';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import Loader from '@/components/Loader';
import H1 from '@/components/typography/H1';
import styled from 'styled-components';

const CategoryTitle = styled(H1)`
  margin-bottom: ${({ theme }) => theme.space.md};

  /* Screen reader only - visually hidden but accessible */
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Browse: React.FC = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const { childId, path } = useParams<{ childId: string; path: string }>();
  const { isPending: isLoading, error, data } = useFetchBrowse();
  useMetaTitle();

  const topLevelItem = React.useMemo(() => {
    if (data?.status === 200) {
      const product = data.data[0];
      const item = path
        ? product?.children?.find(item => item.topicId === path)
        : data.data[0];

      if (item) {
        sessionStorage.setItem(`browse_state_${path}`, JSON.stringify(item));
      }
      return item;
    }

    const cached = sessionStorage.getItem(`browse_state_${path}`);
    return cached ? JSON.parse(cached) : undefined;
  }, [data, path]);

  React.useEffect(() => {
    trackPageView({});
  }, [trackPageView]);

  const { breadcrumbs } = useNodes(
    topLevelItem,
    childId,
    path,
    topLevelItem?.title ?? '',
  );

  if (isLoading && !topLevelItem) {
    return <Loader />;
  }

  return (
    <Page>
      <PageContentWrapper flex={1}>
        <PageContentRow $center={true}>
          <MainContent>
            {/* H1 for accessibility - visually hidden but provides page heading */}
            <CategoryTitle>
              {topLevelItem?.title || t('browse.categories')}
            </CategoryTitle>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            {!error && (data?.status === 200 || topLevelItem) ? (
              <BrowseContent
                parentNode={topLevelItem}
                childId={childId}
                path={path}
                loading={isLoading && !topLevelItem}
              />
            ) : (
              <div>{t('browse.errormessage')}</div>
            )}
          </MainContent>
        </PageContentRow>
      </PageContentWrapper>
    </Page>
  );
};

export default Browse;
